import { Badge, Card, Heading, IconButton, Input, LuIcon, Menu, Separator, Text, toast } from "@overmap-ai/blocks"
import {
	IssueStatusType,
	type IssueType,
	type IssueTypeStatus,
	OrganizationAccessLevel,
	type Stored,
	ISSUE_STATUS_TYPES,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectIssueTypeById, selectIssueTypeStatusesOfIssueType } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback, useMemo } from "react"

import { ColorButton } from "../../components/Buttons/ColorButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateIssueTypeStatusDialog } from "../../components/Dialogs/CreateIssueTypeStatusDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { ColorPickerPopover } from "../../components/Popovers/ColorPickerPopover"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { issueStatusTypeData } from "../../mappings/issues"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId/statuses")

export const IssueTypeSettingsStatusView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const issueTypeStatuses = useShallowEqaulSelector(selectIssueTypeStatusesOfIssueType(issueType.uuid))

	const statusesByType = useMemo(() => {
		const grouped = new Map<IssueStatusType, Stored<IssueTypeStatus>[]>()

		ISSUE_STATUS_TYPES.forEach((type) => grouped.set(type, []))

		issueTypeStatuses.forEach((status) => {
			grouped.get(status.type)?.push(status)
		})

		return grouped
	}, [issueTypeStatuses])

	const handleSubmit = useCallback(
		(values: Pick<IssueTypeStatus, "color" | "name" | "type">) => {
			const existingStatusesOfType = statusesByType.get(values.type) ?? []
			const nextOrder = existingStatusesOfType.length

			const [, promise] = sdk.issueTypeStatuses.add({
				issue_type: issueType.uuid,
				name: values.name,
				color: values.color,
				type: values.type,
				order: nextOrder,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to create status",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueType.uuid, statusesByType],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Statuses</Heading>
					<Text size="sm" accentColor="base">
						Manage issue type statuses.
					</Text>
				</div>

				<Separator size="full" />

				<Card className="flex flex-col gap-4" size="sm">
					{ISSUE_STATUS_TYPES.map((statusType) => {
						const statuses = statusesByType.get(statusType) ?? []
						const { color, label } = issueStatusTypeData[statusType]

						return (
							<div key={statusType} className="flex flex-col gap-4">
								<Card
									variant="soft"
									className="px-4! flex items-center justify-between gap-2"
									size="xs"
								>
									<Text accentColor="base">{label}</Text>
									<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
										<CreateIssueTypeStatusDialog
											onSubmit={handleSubmit}
											defaultColor={color}
											statusType={statusType}
											size="sm"
										>
											<IconButton variant="ghost" size="sm" accentColor="base">
												<LuIcon icon={modelIconMapping.add} />
											</IconButton>
										</CreateIssueTypeStatusDialog>
									</OrganizationAccessRequired>
								</Card>

								{statuses.length > 0 && (
									<ol className="flex flex-col gap-4">
										{statuses.map((issueTypeStatus) => (
											<IssueTypeStatusListItem
												key={issueTypeStatus.uuid}
												issueTypeStatus={issueTypeStatus}
												issueType={issueType}
											/>
										))}
									</ol>
								)}
							</div>
						)
					})}
				</Card>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsStatusView.displayName = "IssueTypeSettingsStatusView"

interface IssueTypeStatusListItemProps {
	issueTypeStatus: Stored<IssueTypeStatus>
	issueType: Stored<IssueType>
}

const IssueTypeStatusListItem = memo<IssueTypeStatusListItemProps>((props) => {
	const { issueTypeStatus, issueType } = props

	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const isDefault = issueType.default_status === issueTypeStatus.uuid
	const typeData = issueStatusTypeData[issueTypeStatus.type]

	const handleNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value

			if (issueTypeStatus.name === value) return

			const [, promise] = sdk.issueTypeStatuses.update({
				uuid: issueTypeStatus.uuid,
				name: e.target.value,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to update status",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueTypeStatus.uuid, issueTypeStatus.name],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(issueTypeStatus.name, handleNameInputBlur, true)

	const handleColorChange = useCallback(
		(color: string) => {
			const [, promise] = sdk.issueTypeStatuses.update({
				uuid: issueTypeStatus.uuid,
				color: color,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to update status color",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueTypeStatus.uuid],
	)

	const handleSetDefault = useCallback(() => {
		const [, promise] = sdk.issueTypes.update({
			uuid: issueType.uuid,
			default_status: issueTypeStatus.uuid,
		})

		promise.catch((error) => {
			toast.error({
				title: "Failed to set default status",
				description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
			})
		})
	}, [issueType.uuid, issueTypeStatus.uuid])

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete status",
			description: "The status will be deleted immediately. This action cannot be undone.",
			onAction: () =>
				sdk.issueTypeStatuses.delete(issueTypeStatus.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete status",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				}),
		})
	}, [issueTypeStatus.uuid, openConfirmDeleteDialog])

	return (
		<li className="flex items-center justify-between gap-2">
			<div className="flex grow items-center gap-2">
				<ColorPickerPopover required color={issueTypeStatus.color} onColorChange={handleColorChange} size="sm">
					<ColorButton disabled={!isAdmin} color={`${issueTypeStatus.color}40`}>
						<LuIcon icon={typeData.icon} color={issueTypeStatus.color} />
					</ColorButton>
				</ColorPickerPopover>

				<Input.Root variant="outline" className="grow">
					<Input.Field placeholder="Enter a name" disabled={!isAdmin} {...nameInputProps} />
				</Input.Root>
			</div>

			<div className="flex items-center gap-1">
				{isDefault && (
					<Badge variant="soft" size="sm" accentColor="base">
						Default
					</Badge>
				)}

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Menu.Root align="end">
						<Menu.ClickTrigger>
							<IconButton accentColor="base" variant="ghost" size="sm">
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleSetDefault}>
								<LuIcon icon={modelIconMapping.copy} />
								Make default
							</Menu.Item>
							<Menu.Separator />
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} /> Delete
							</Menu.Item>
						</Menu.Content>
					</Menu.Root>
				</OrganizationAccessRequired>
			</div>
		</li>
	)
})
IssueTypeStatusListItem.displayName = "IssueTypeStatusListItem"
